import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";

import { getPossibleQueueMembers, getQueueMemberLog, getQueues } from "@/api/stackApi";
import { HELP_LOGS_QUEUE_MEMBERS } from "@/constants/HelpUrls";
import Avatar from "@/design/components/Avatar";
import FabBar from "@/design/components/FabBar";
import MessageStack from "@/design/components/MessageStack";
import Page from "@/design/layouts/Page";
import { setHelpUrl } from "@/store/reducers/ui";
import useAccessibleColors from "@/utils/hooks/useAccessibleColors";
import useNow from "@/utils/hooks/useNow";
import Valkyrie, {
  viArrowRightFromBracket,
  viArrowRightToBracket,
  viArrowRightToBracketClock,
  viCalendar,
  viCircleXmark,
  viList,
  viLocationPinDot,
  viPause,
  viPerson,
  viPlay,
} from "@sippy-platform/valkyrie";

import { Box, Card, CircularProgress, Container, Divider, Pagination, Paper } from "@mui/material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  differenceInDays,
  differenceInSeconds,
  endOfDay,
  format,
  isValid,
  isWithinInterval,
  parseISO,
  startOfDay,
} from "date-fns";

import ClearFilterAction from "../_Components/ClearFilterAction";
import DataCell from "../_Components/DataCell";
import DateFilter from "../_Components/DateFilter";
import FilterBar from "../_Components/FilterBar";
import FilterDropdown from "../_Components/FilterDropdown";
import RefreshActions from "../_Components/RefreshActions";
import TableHead from "../_Components/TableHead";
import TableHeading from "../_Components/TableHeading";
import TableRow from "../_Components/TableRow";

export default function FlexDesKLog() {
  const dispatch = useDispatch();
  const colors = useAccessibleColors();
  const [searchParams, setSearchParams] = useSearchParams();

  const pbx = useSelector((state) => state.domain.pbx);
  const logSettings = useSelector((state) => state.settings.logs);

  const dateFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS";

  useEffect(() => {
    dispatch(setHelpUrl(HELP_LOGS_QUEUE_MEMBERS));
  }, [dispatch]);

  // Filter preparations
  const queuesQuery = useQuery({
    queryKey: [pbx.context, "queues"],
    queryFn: getQueues,
    refetchInterval: 10 * 1000 * 60,
  });
  const possibleQueueMembers = useQuery({
    queryKey: [pbx.context, "queues.members"],
    queryFn: getPossibleQueueMembers,
    refetchInterval: 10 * 1000 * 60,
  });

  // Filters
  const now = useNow();
  const { page, order, orderBy, startDate, endDate, queue, member } = useMemo(
    () => ({
      page: Number(searchParams.get("page") ?? 0),
      order: searchParams.get("order") === "asc" ? "asc" : "desc",
      orderBy: ["date", "queueName", "memberName", "event"].includes(searchParams.get("orderBy"))
        ? searchParams.get("orderBy")
        : "date",
      startDate:
        searchParams.get("startDate") && isValid(parseISO(searchParams.get("startDate")))
          ? searchParams.get("startDate")
          : format(startOfDay(now), dateFormat),
      endDate:
        searchParams.get("endDate") && isValid(parseISO(searchParams.get("endDate")))
          ? searchParams.get("endDate")
          : format(endOfDay(now), dateFormat),
      member: possibleQueueMembers.data?.find((member) => searchParams.get("member") === member.attributes.exten)
        ? searchParams.get("member")
        : "",
      queue: queuesQuery.data?.find((device) => searchParams.get("queue") === device.exten)
        ? searchParams.get("queue")
        : "",
    }),
    [now, queuesQuery.data, searchParams, possibleQueueMembers.data],
  );

  const isFiltering = useMemo(
    () =>
      !(
        page === 0 &&
        order === "desc" &&
        orderBy === "date" &&
        startDate === format(startOfDay(now), dateFormat) &&
        endDate === format(endOfDay(now), dateFormat) &&
        member === "" &&
        queue === ""
      ),
    [now, page, order, orderBy, startDate, endDate, member, queue],
  );

  const setFilter = useCallback(
    (key, value, resetPage = true) => {
      searchParams.set(key, value);
      if (resetPage) searchParams.set("page", 0);

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  // Check if the selected date range is invalid (either more than 31 days + 1 hour (because DST) or non-positive duration)
  const invalidDateRange =
    differenceInSeconds(parseISO(endDate), parseISO(startDate)) > 31 * 24 * 60 * 60 + 60 * 60 ||
    differenceInSeconds(parseISO(endDate), parseISO(startDate)) <= 0;

  // Query
  const pauseRefresh = !isWithinInterval(now, { start: parseISO(startDate), end: parseISO(endDate) });
  const queueMemberLogQuery = useQuery({
    queryKey: [pbx.context, "log.queueMembers", page, order, orderBy, startDate, endDate, queue, member],
    queryFn: () =>
      getQueueMemberLog({
        page,
        order,
        orderBy,
        startDate,
        endDate,
        queue,
        member,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: pauseRefresh ? 0 : logSettings.refreshInterval * 1000 * 60,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: pauseRefresh || logSettings.refreshInterval === 0 ? false : true,
    enabled:
      isValid(parseISO(startDate)) &&
      isValid(parseISO(endDate)) &&
      differenceInDays(parseISO(endDate), parseISO(startDate)) <= 31,
  });

  // Handlers
  const handlePage = (event, page) => {
    setFilter("page", page - 1, false);
  };

  return (
    <Page sx={{ pt: 8, position: "relative" }}>
      <title>{`Queue member log \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>

      <FilterBar>
        <ClearFilterAction disabled={isFiltering} />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <DateFilter startDate={startDate} endDate={endDate} setFilter={setFilter} />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <FilterDropdown
          id="member"
          icon={viPerson}
          options={possibleQueueMembers.data || []}
          defaultOptions={[{ name: "All members", value: "" }]}
          value={member}
          setValue={(value) => setFilter("member", value)}
          getOptionLabel={(x) => x.attributes?.name ?? x.name}
          getOptionLabelSecondary={(x) => x.attributes?.exten}
          getOptionValue={(x) => x.attributes?.exten ?? x.value}
          getAvatarIcon={(x) => (x.type === "customDestination" ? viLocationPinDot : undefined)}
          searchKeys={["attributes.name", "attributes.exten"]}
        />
        <FilterDropdown
          id="queue"
          icon={viList}
          options={queuesQuery.data || []}
          defaultOptions={[{ name: "All queues", value: "" }]}
          value={queue}
          setValue={(value) => setFilter("queue", value)}
          getOptionLabel={(x) => x.name}
          getOptionLabelSecondary={(x) => x.exten}
          getOptionValue={(x) => x.value ?? x.exten}
          getAvatarIcon={() => viList}
          searchKeys={["name", "exten"]}
        />
        <Box sx={{ flexGrow: 1 }} />
        <RefreshActions
          onClick={() => queueMemberLogQuery.refetch()}
          isRefreshing={queueMemberLogQuery.isFetching}
          isPaused={pauseRefresh}
        />
      </FilterBar>

      <Container maxWidth="xl">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, auto)",
            gridTemplateRows: "auto auto",
            gap: 1,
          }}
        >
          <TableHead>
            <TableHeading id="date" setFilter={setFilter} order={order} orderBy={orderBy}>
              Time
            </TableHeading>
            <TableHeading id="queueName" setFilter={setFilter} order={order} orderBy={orderBy}>
              Queue
            </TableHeading>
            <TableHeading id="memberName" setFilter={setFilter} order={order} orderBy={orderBy}>
              Member
            </TableHeading>
            <TableHeading id="event" setFilter={setFilter} order={order} orderBy={orderBy}>
              Event
            </TableHeading>
          </TableHead>
          {invalidDateRange ? (
            <MessageStack icon={<Valkyrie icon={viCalendar} />} message="The current date range is not valid." />
          ) : queueMemberLogQuery.isFetched ? (
            <>
              {queueMemberLogQuery.data?.qml?.length >= 1 ? (
                <Card sx={{ display: "grid", gridTemplateColumns: "subgrid", gridColumn: "1 / -1", mb: 6.5 }}>
                  {queueMemberLogQuery.data?.qml.map((queueMemberEvent, eventKey) => {
                    let event = "Unknown";

                    switch (queueMemberEvent.event) {
                      case "LOGOUT":
                        event = "Logout";
                        break;
                      case "LOGIN":
                        event = "Login";
                        break;
                      case "PAUSE":
                        event = "Pause";
                        break;
                      case "UNPAUSE":
                        event = "Unpause";
                        break;
                    }

                    return (
                      <TableRow key={eventKey}>
                        <DataCell
                          invertOrder
                          noWrap
                          tabular
                          primary={format(queueMemberEvent.date, "pp")}
                          secondary={format(queueMemberEvent.date, "PPP")}
                        />
                        <DataCell
                          startDecorator={<Avatar text={queueMemberEvent.queueName} px={24} icon={viList} />}
                          primary={queueMemberEvent.queueName}
                          secondary={queueMemberEvent.queueNumber}
                        />
                        <DataCell
                          startDecorator={<Avatar text={queueMemberEvent.memberName} px={24} />}
                          primary={queueMemberEvent.memberName}
                          secondary={queueMemberEvent.memberNumber}
                        />
                        <DataCell
                          startDecorator={
                            <Valkyrie
                              icon={
                                event === "Login"
                                  ? viArrowRightToBracket
                                  : event === "Pause"
                                    ? viPause
                                    : event === "Unpause"
                                      ? viPlay
                                      : viArrowRightFromBracket
                              }
                              style={{
                                color: ["Login", "Unpause"].includes(event)
                                  ? colors.green
                                  : event === "Pause"
                                    ? colors.amber
                                    : colors.red,
                              }}
                            />
                          }
                          primary={event}
                          secondary={
                            queueMemberEvent.performedByName
                              ? ` by ${queueMemberEvent.performedByName} \u00b7 ${queueMemberEvent.performedByNumber ? queueMemberEvent.performedByNumber : "My Sippy"}`
                              : undefined
                          }
                        />
                      </TableRow>
                    );
                  })}
                </Card>
              ) : (
                <MessageStack
                  icon={<Valkyrie icon={viArrowRightToBracketClock} />}
                  message="No data for your current filters..."
                />
              )}
            </>
          ) : queueMemberLogQuery.isError ? (
            <MessageStack
              icon={<Valkyrie icon={viCircleXmark} />}
              color="error"
              message="We couldn't finish your request..."
              description={queueMemberLogQuery?.error?.message}
            />
          ) : (
            <MessageStack icon={<CircularProgress size={36} />} message="Getting your data..." />
          )}
        </Box>

        <FabBar sx={{ px: 1, justifyContent: "center" }}>
          <Paper
            variant="acrylic"
            sx={{
              whiteSpace: "nowrap",
              display: {
                xs: "none",
                md: "flex",
              },
              alignItems: "center",
              justifyContent: "center",
              px: 1.5,
              py: 0.75,
            }}
          >
            <Box>
              {page * 50 + 1}-
              {(page + 1) * 50 > queueMemberLogQuery.data?.count ? queueMemberLogQuery.data?.count : (page + 1) * 50} of{" "}
              {queueMemberLogQuery.data?.count}
            </Box>
          </Paper>
          {Math.ceil(queueMemberLogQuery.data?.count / 50) > 1 && (
            <Pagination count={Math.ceil(queueMemberLogQuery.data?.count / 50)} page={page + 1} onChange={handlePage} />
          )}
        </FabBar>
      </Container>
    </Page>
  );
}
